import type { LostSaleCreate, LostSaleReason } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { api, newIdempotencyKey } from '../api';
import { useAuth } from '../auth';
import { PeriodBar, presets, usePeriod } from '../components/PeriodBar';
import { ErrorBox, QueryView } from '../components/ui';
import { formatDate, formatMoney, formatQty, todayIso } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';
import { parseNumber } from './StockPage';

const REASONS: LostSaleReason[] = ['out_of_stock', 'price', 'quality', 'late', 'competitor', 'not_carried', 'other'];
const CHANNELS = ['phone', 'visit', 'text', 'whatsapp', 'walkin', 'email', 'other'] as const;
const RANGES = presets('d30', 'd7', 'd90', 'd365');

/**
 * Lost sales (decision 97): the sale that did not happen and why, recorded in one go by whoever heard it, and what
 * the period lost by reason, by item and by customer. The office, sales, the dock and the drivers record; the office,
 * sales and readonly read the report.
 */
export function LostSalesPage() {
  const { t } = useLocale();
  const { me } = useAuth();
  const reads = me?.role === 'owner' || me?.role === 'office' || me?.role === 'sales' || me?.role === 'readonly';
  const records = me?.role !== 'readonly' && me?.role !== 'customer';
  return (
    <div className="desk-page" data-testid="lost-sales">
      <header className="desk-head">
        <div>
          <h1>{t('lostSalesTitle')}</h1>
          <p className="muted">{t('lostSalesHint')}</p>
        </div>
      </header>
      {records ? <LostSaleForm /> : null}
      {reads ? <LostSalesReportView /> : null}
    </div>
  );
}

function LostSaleForm() {
  const { t } = useLocale();
  const qc = useQueryClient();
  const items = useQuery({ queryKey: qk.items(''), queryFn: ({ signal }) => api.items('', signal), staleTime: 5 * 60_000 });
  const customers = useQuery({ queryKey: qk.customers(''), queryFn: ({ signal }) => api.customers('', signal), staleTime: 5 * 60_000 });
  const blank = { day: todayIso(), customer: '', item: '', qty: '', reason: null as LostSaleReason | null, quoted: '', theirs: '', competitor: '', channel: 'phone' as (typeof CHANNELS)[number], note: '' };
  const [f, setF] = useState(blank);
  const [done, setDone] = useState(false);
  const itemByLabel = useMemo(() => new Map((items.data ?? []).map((i) => [i.name.toLowerCase(), i.id])), [items.data]);
  const customerByName = useMemo(() => new Map((customers.data ?? []).map((c) => [c.name.toLowerCase(), c.id])), [customers.data]);
  const itemId = itemByLabel.get(f.item.trim().toLowerCase());
  const packUnit = (items.data ?? []).find((i) => i.id === itemId)?.packUnit;
  const customerId = customerByName.get(f.customer.trim().toLowerCase());
  const qty = f.qty.trim() ? parseNumber(f.qty) : undefined;
  const quoted = f.quoted.trim() ? parseNumber(f.quoted) : undefined;
  const theirs = f.theirs.trim() ? parseNumber(f.theirs) : undefined;
  const unknownCustomer = f.customer.trim() !== '' && !customerId;
  const badNumber = [qty, quoted, theirs].some((n) => n === null) || (qty !== undefined && qty !== null && qty <= 0);
  const ready = !!f.reason && f.item.trim() !== '' && !unknownCustomer && !badNumber;
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const save = useMutation({
    mutationFn: (body: LostSaleCreate) => {
      const serialized = JSON.stringify(body);
      if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
      return api.recordLostSale(body, attempt.current.key);
    },
    onSuccess: () => {
      attempt.current = null;
      setF(blank);
      setDone(true);
      void qc.invalidateQueries({ queryKey: ['lost-sales'] });
    },
  });
  const submit = () => {
    if (!ready || !f.reason) return;
    setDone(false);
    save.mutate({
      reason: f.reason,
      channel: f.channel,
      ...(f.day && f.day !== todayIso() ? { occurredOn: f.day } : {}),
      ...(itemId ? { itemId } : { itemText: f.item.trim() }),
      ...(customerId ? { customerId } : {}),
      ...(qty ? { qty } : {}),
      ...(quoted !== undefined && quoted !== null ? { quotedPrice: quoted } : {}),
      ...(theirs !== undefined && theirs !== null ? { theirPrice: theirs } : {}),
      ...(f.competitor.trim() ? { competitor: f.competitor.trim() } : {}),
      ...(f.note.trim() ? { note: f.note.trim() } : {}),
    });
  };

  return (
    <form
      className="panel stack-sm"
      data-testid="lost-sale-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <h2>{t('lostSaleRecord')}</h2>
      <fieldset className="choice">
        <legend className="label">{t('lostSaleWhy')}</legend>
        <div className="seg seg-wrap" role="group" aria-label={t('lostSaleWhy')}>
          {REASONS.map((r) => (
            <button key={r} type="button" aria-pressed={f.reason === r} onClick={() => setF({ ...f, reason: r })}>
              {t(`lostReason_${r}` as AppMessageKey)}
            </button>
          ))}
        </div>
      </fieldset>
      <div className="form-grid">
        <label className="field">
          <span>{t('lostSaleItem')}</span>
          <input type="text" list="lost-items" value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} placeholder={t('lostSaleItemPlaceholder')} />
          <datalist id="lost-items">
            {(items.data ?? []).map((i) => (
              <option key={i.id} value={i.name} />
            ))}
          </datalist>
          {f.item.trim() && !itemId ? <small className="muted">{t('lostSaleNotInList')}</small> : null}
        </label>
        <label className="field">
          <span>{t('lostSaleCustomer')}</span>
          <input type="text" list="lost-customers" value={f.customer} onChange={(e) => setF({ ...f, customer: e.target.value })} placeholder={t('lostSaleCustomerPlaceholder')} aria-invalid={unknownCustomer || undefined} />
          <datalist id="lost-customers">
            {(customers.data ?? []).map((c) => (
              <option key={c.id} value={c.name} />
            ))}
          </datalist>
          {unknownCustomer ? <small className="field-error">{t('lostSaleUnknownCustomer')}</small> : null}
        </label>
        <label className="field">
          <span>{t('lostSaleDay')}</span>
          <input type="date" value={f.day} max={todayIso()} onChange={(e) => setF({ ...f, day: e.target.value })} />
        </label>
        <label className="field">
          <span>
            {t('lostSaleQty')}
            {packUnit ? ` (${packUnit})` : ''}
          </span>
          <input type="text" inputMode="decimal" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} placeholder="0" />
        </label>
        <label className="field">
          <span>{t('lostSaleQuoted')}</span>
          <input type="text" inputMode="decimal" value={f.quoted} onChange={(e) => setF({ ...f, quoted: e.target.value })} placeholder="0.00" />
        </label>
        <label className="field">
          <span>{t('lostSaleTheirs')}</span>
          <input type="text" inputMode="decimal" value={f.theirs} onChange={(e) => setF({ ...f, theirs: e.target.value })} placeholder="0.00" />
        </label>
        <label className="field">
          <span>{t('lostSaleCompetitor')}</span>
          <input type="text" maxLength={120} value={f.competitor} onChange={(e) => setF({ ...f, competitor: e.target.value })} />
        </label>
        <label className="field">
          <span>{t('lostSaleChannel')}</span>
          <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value as (typeof CHANNELS)[number] })}>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {t(`lostChannel_${c}` as AppMessageKey)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('lostSaleNote')}</span>
          <input type="text" maxLength={500} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </label>
      </div>
      {badNumber ? <p className="field-error">{t('lostSaleBadNumber')}</p> : null}
      {save.error ? <ErrorBox error={save.error} /> : null}
      {done ? (
        <p className="note" role="status">
          {t('lostSaleSaved')}
        </p>
      ) : null}
      <div className="row-actions">
        <button type="submit" className="btn btn-primary" disabled={!ready || save.isPending}>
          {t('lostSaleSave')}
        </button>
      </div>
    </form>
  );
}

function LostSalesReportView() {
  const { t, intlLocale } = useLocale();
  const period = usePeriod(RANGES);
  const report = useQuery({ queryKey: qk.lostSales(period.from, period.to), queryFn: ({ signal }) => api.lostSales({ from: period.from, to: period.to }, signal) });
  const money = (v: number) => formatMoney(v, intlLocale);
  return (
    <section className="stack">
      <div className="toolbar">
        <PeriodBar presets={RANGES} period={period} />
      </div>
      <QueryView query={report}>
        {(r) => (
          <div className="stack">
            <div className="kpi-row">
              <div className="kpi">
                <span className="kpi-label">{t('lostSalesCount')}</span>
                <strong>{r.total.count}</strong>
              </div>
              <div className="kpi">
                <span className="kpi-label">{t('lostSalesValue')}</span>
                <strong>{money(r.total.value)}</strong>
              </div>
              {r.byReason.slice(0, 3).map((x) => (
                <div key={x.reason} className="kpi">
                  <span className="kpi-label">{t(`lostReason_${x.reason}` as AppMessageKey)}</span>
                  <strong>
                    {x.count}
                    {x.value ? ` · ${money(x.value)}` : ''}
                  </strong>
                </div>
              ))}
            </div>
            {r.total.unvalued ? <p className="muted">{t('lostSalesUnvalued').replace('{n}', String(r.total.unvalued))}</p> : null}
            {r.truncated ? <p className="warn-band">{t('lostSalesTruncated')}</p> : null}
            {r.rows.length === 0 ? <p className="muted">{t('lostSalesNone')}</p> : null}
            {r.byItem.length ? (
              <div className="table-wrap">
                <table className="data-table" data-testid="lost-by-item">
                  <caption>{t('lostSalesByItem')}</caption>
                  <thead>
                    <tr>
                      <th>{t('itemCol')}</th>
                      <th className="al-right">{t('lostSalesTimes')}</th>
                      <th className="al-right">{t('qty')}</th>
                      <th className="al-right">{t('lostSalesValue')}</th>
                      <th className="al-right">{t('lostReason_out_of_stock')}</th>
                      <th className="al-right">{t('lostReason_not_carried')}</th>
                      <th className="al-right">{t('lostReason_price')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.byItem.map((x) => (
                      <tr key={x.itemId ?? x.itemName}>
                        <td>{x.itemName}</td>
                        <td className="al-right">{x.count}</td>
                        <td className="al-right">{formatQty(x.qty)}</td>
                        {/* no price to value it at: unknown, not nothing */}
                        <td className="al-right">{x.value ? money(x.value) : '—'}</td>
                        <td className="al-right">{x.outOfStock || ''}</td>
                        <td className="al-right">{x.notCarried || ''}</td>
                        <td className="al-right">{x.price || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {r.rows.length ? (
              <div className="table-wrap">
                <table className="data-table" data-testid="lost-rows">
                  <caption>{t('lostSalesEvery')}</caption>
                  <thead>
                    <tr>
                      <th>{t('dateCol')}</th>
                      <th>{t('lostSaleCustomer')}</th>
                      <th>{t('itemCol')}</th>
                      <th className="al-right">{t('qty')}</th>
                      <th>{t('lostSaleWhy')}</th>
                      <th className="al-right">{t('lostSaleQuoted')}</th>
                      <th className="al-right">{t('lostSaleTheirs')}</th>
                      <th>{t('lostSaleNote')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.rows.map((x) => (
                      <tr key={x.id}>
                        <td>{formatDate(x.occurredOn, intlLocale, 'date')}</td>
                        <td>{x.customerName ?? '—'}</td>
                        <td>{x.itemName}</td>
                        <td className="al-right">{x.qty === null ? '' : `${formatQty(x.qty)} ${x.unit ?? ''}`}</td>
                        <td>{t(`lostReason_${x.reason}` as AppMessageKey)}</td>
                        <td className="al-right">{x.quotedPrice === null ? '' : money(x.quotedPrice)}</td>
                        <td className="al-right">{x.theirPrice === null ? '' : money(x.theirPrice)}</td>
                        <td>{[x.competitor, x.note].filter(Boolean).join(' · ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        )}
      </QueryView>
    </section>
  );
}
